import { Injectable } from '@angular/core';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { Observable, from, Subject } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import { TenantService } from '../tenant.service';

const AGENT_BASE = 'https://flaredeck-agent-861259873190.us-central1.run.app';

export interface AIChatRequest {
  message: string;
  sessionId?: string;
}

export interface AIChatResponse {
  response: string;
  sessionId: string;
  toolsUsed?: string[];
}

export interface ChatSession {
  id: string;
  sessionId?: string;
  title: string;
  createdAt: Date;
  updatedAt: Date;
  userId: string;
  tenantId: string;
}

export interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
}

export interface ChatSessionWithMessages {
  session: ChatSession;
  messages: ChatMessage[];
}

export interface StreamEvent {
  type: 'text' | 'done' | 'error';
  content?: string;
  sessionId?: string;
  message?: string;
}

@Injectable({
  providedIn: 'root',
})
export class AIAgentChatService {
  constructor(
    private firestore: AngularFirestore,
    private auth: AngularFireAuth,
    private http: HttpClient,
    private tenantService: TenantService
  ) {}

  private get activeTenantId(): string | null {
    return this.tenantService.getCurrentTenantId();
  }

  /**
   * Send a message and get SSE streaming response
   */
  public sendMessageStream(message: string, sessionId?: string): Observable<StreamEvent> {
    const subject = new Subject<StreamEvent>();

    this.auth.currentUser.then(async (user) => {
      if (!user) {
        subject.error(new Error('Not authenticated'));
        return;
      }

      const token = await user.getIdToken();

      const response = await fetch(`${AGENT_BASE}/chat/stream`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ message, sessionId, tenantId: this.activeTenantId }),
      });

      if (!response.ok) {
        subject.error(new Error(`HTTP ${response.status}`));
        return;
      }

      const reader = response.body!.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const event: StreamEvent = JSON.parse(line.slice(6));
              subject.next(event);
            } catch {}
          }
        }
      }

      subject.complete();
    }).catch(err => subject.error(err));

    return subject.asObservable();
  }

  /**
   * Send a message (non-streaming, backwards compatible)
   */
  public sendMessage(message: string, sessionId?: string): Observable<AIChatResponse> {
    return from(this.auth.currentUser).pipe(
      switchMap(user => {
        if (!user) throw new Error('Not authenticated');
        return from(user.getIdToken()).pipe(
          switchMap(token =>
            this.http.post<AIChatResponse>(`${AGENT_BASE}/chat/message`, { message, sessionId, tenantId: this.activeTenantId }, {
              headers: { 'Authorization': `Bearer ${token}` },
            })
          )
        );
      })
    );
  }

  /**
   * List all chat sessions for the current user - reads directly from Firestore
   */
  public listSessions(limit: number = 20): Observable<ChatSession[]> {
    return this.auth.user.pipe(
      switchMap(user => {
        if (!user) throw new Error('User not authenticated');
        return user.getIdTokenResult().then(token => {
          const tenantId = this.activeTenantId || token.claims['tenantId'] as string;
          const userId = user.uid;
          if (!tenantId) throw new Error('User does not have a tenant');

          const path = `${tenantId}/data/chatSessions/${userId}/sessions`;
          return this.firestore
            .collection<ChatSession>(path, ref => ref.orderBy('lastMessageAt', 'desc').limit(limit))
            .snapshotChanges()
            .pipe(map(actions => actions.map(a => ({ ...a.payload.doc.data(), id: a.payload.doc.id }))));
        });
      }),
      switchMap(observable => observable)
    );
  }

  /**
   * Get a specific chat session with its messages - reads directly from Firestore
   */
  public getSession(sessionId: string): Observable<ChatSessionWithMessages> {
    return this.auth.user.pipe(
      switchMap(user => {
        if (!user) throw new Error('User not authenticated');
        return from(user.getIdTokenResult()).pipe(
          switchMap(token => {
            const tenantId = this.activeTenantId || token.claims['tenantId'] as string;
            const userId = user.uid;
            if (!tenantId) throw new Error('User does not have a tenant');

            const sessionPath = `${tenantId}/data/chatSessions/${userId}/sessions/${sessionId}`;
            const messagesPath = `${sessionPath}/messages`;

            const session$ = this.firestore.doc<ChatSession>(sessionPath).get().pipe(
              map(doc => {
                if (!doc.exists) throw new Error('Session not found');
                return { ...doc.data(), id: doc.id } as ChatSession;
              })
            );

            const messages$ = this.firestore
              .collection<ChatMessage>(messagesPath, ref => ref.orderBy('timestamp', 'asc'))
              .get()
              .pipe(map(snapshot => snapshot.docs.map(doc => doc.data())));

            return from(Promise.all([session$.toPromise(), messages$.toPromise()])).pipe(
              map(([session, messages]) => ({ session: session!, messages: messages! }))
            );
          })
        );
      })
    );
  }

  /**
   * Delete a chat session
   */
  public deleteSession(sessionId: string): Observable<void> {
    return from(this.auth.currentUser).pipe(
      switchMap(user => {
        if (!user) throw new Error('Not authenticated');
        return from(user.getIdToken()).pipe(
          switchMap(token =>
            this.http.delete<void>(`${AGENT_BASE}/chat/sessions/${sessionId}`, {
              headers: { 'Authorization': `Bearer ${token}` },
            })
          )
        );
      })
    );
  }

  /**
   * Update a chat session title
   */
  public updateSessionTitle(sessionId: string, title: string): Observable<void> {
    return from(this.auth.currentUser).pipe(
      switchMap(user => {
        if (!user) throw new Error('Not authenticated');
        return from(user.getIdToken()).pipe(
          switchMap(token =>
            this.http.patch<void>(`${AGENT_BASE}/chat/sessions/${sessionId}`, { title }, {
              headers: { 'Authorization': `Bearer ${token}` },
            })
          )
        );
      })
    );
  }
}
